import css from "./DevicesBrowser.sass?inline"
import {isDefined, Lifecycle, panic, RuntimeNotifier} from "@opendaw/lib-std"
import {Html} from "@opendaw/lib-dom"
import {createElement, RouteLocation} from "@opendaw/lib-jsx"
import {DeviceHost, Devices, InstrumentFactories} from "@opendaw/studio-adapters"
import {ContextMenu, EffectFactories, EffectFactory, MenuItem, Project} from "@opendaw/studio-core"
import {StudioService} from "@/service/StudioService.ts"
import {DragAndDrop} from "@/ui/DragAndDrop"
import {DragDevice} from "@/ui/AnyDragData"
import {TextTooltip} from "@/ui/surface/TextTooltip"
import {Icon} from "../components/Icon"
import {DefaultInstrumentFactory} from "@/ui/defaults/DefaultInstrumentFactory"

const className = Html.adoptStyleSheet(css, "DevicesBrowser")

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
}

export const DevicesBrowser = ({lifecycle, service}: Construct) => {
    const {project} = service
    return (
        <div className={className}>
            <div className="resources">
                <section className="instrument">
                    <h1>Instruments</h1>
                    {createInstrumentList(lifecycle, project)}
                </section>
                <section className="audio">
                    <h1>Audio Effects</h1>
                    {createEffectList(lifecycle, project, EffectFactories.AudioNamed, "audio-effect")}
                </section>
                <section className="midi">
                    <h1>Midi Effects</h1>
                    {createEffectList(lifecycle, project, EffectFactories.MidiNamed, "midi-effect")}
                </section>
            </div>
            <div className="manual help-section">
                <section>
                    <h1>Creating an Instrument</h1>
                    <p>
                        To start making sound, click on an instrument from the list. This will create a new instance in
                        your
                        project.
                    </p>
                </section>
                <section>
                    <h1>Adding EffectFactories</h1>
                    <p>
                        Once an instrument is created, you can add effects. To do this, simply drag an effect
                        from the list and drop it into the instrument's device chain.
                    </p>
                </section>
            </div>
        </div>
    )
}

const createInstrumentList = (lifecycle: Lifecycle, project: Project) => (
    <ul>{Object.entries(InstrumentFactories.Named).map(([key, factory]) => {
        const element = (
            <li onclick={() => project.editing.modify(() => DefaultInstrumentFactory.create(project.api, factory))}>
                <div className="icon">
                    <Icon symbol={factory.defaultIcon}/>
                </div>
                {factory.defaultName}
                <span className="brief help-section">{factory.briefDescription}</span>
            </li>
        )
        lifecycle.ownAll(
            DragAndDrop.installSource(element, () => ({
                type: "instrument",
                device: key as InstrumentFactories.Keys
            } satisfies DragDevice)),
            TextTooltip.simple(element, () => {
                const {bottom, left} = element.getBoundingClientRect()
                return {clientX: left, clientY: bottom + 12, text: factory.description}
            })
        )
        return element
    })
    }</ul>
)

const createEffectList = <
    R extends Record<string, EffectFactory>,
    T extends DragDevice["type"]>(lifecycle: Lifecycle, project: Project, records: R, type: T): HTMLUListElement => {
    const entries = Object.entries(records)
    const internal = entries.filter(([_, entry]) => !entry.external)
    const external = entries.filter(([_, entry]) => entry.external)
    const createItem = ([key, entry]: [string, EffectFactory]) => {
        const element = (
            <li onInit={element => {
                lifecycle.own(ContextMenu.subscribe(element, collector => collector.addItems(MenuItem.default({
                    label: `Visit Manual Page for ${entry.defaultName}`, selectable: isDefined(entry.manualPage)
                }).setTriggerProcedure(() => RouteLocation.get().navigateTo(entry.manualPage ?? "/")))))
                element.onclick = () => {
                    const {boxAdapters, editing, userEditingManager} = project
                    const audioUnitOption = userEditingManager.audioUnit.get()
                    if (audioUnitOption.isEmpty()) {
                        RuntimeNotifier.info({
                            headline: "No Source Device Yet",
                            message: "Please create an instrument or select an audio-bus first."
                        }).finally()
                        return
                    }
                    audioUnitOption.ifSome(vertex => {
                        const deviceHost: DeviceHost = boxAdapters.adapterFor(vertex.box, Devices.isHost)
                        if (type === "midi-effect" && deviceHost.inputAdapter.mapOr(input => input.accepts !== "midi", true)) {
                            RuntimeNotifier.info({
                                headline: "Add Midi Effect",
                                message: "The selected audio unit does not have a midi input."
                            }).finally()
                            return
                        }
                        const effectField =
                            type === "audio-effect" ? deviceHost.audioEffects.field()
                                : type === "midi-effect" ? deviceHost.midiEffects.field()
                                    : panic(`Unknown ${type}`)
                        editing.modify(() => entry.create(project, effectField, effectField.pointerHub.incoming().length))
                    })
                }
            }}>
                {entry.external
                    ? <div className="icon external">
                        <img src="/images/tone3000.svg" alt="logo"/>
                    </div>
                    : <div className="icon">
                        <Icon symbol={entry.defaultIcon}/>
                    </div>}
                {entry.defaultName}
                <span className="brief help-section">{entry.briefDescription}</span>
            </li>
        )
        lifecycle.ownAll(
            DragAndDrop.installSource(element, () => ({
                type: type as any,
                start_index: null,
                device: key as keyof typeof EffectFactories.MergedNamed
            } satisfies DragDevice)),
            TextTooltip.simple(element, () => {
                const {bottom, left} = element.getBoundingClientRect()
                return {clientX: left, clientY: bottom + 12, text: entry.description}
            })
        )
        return element
    }
    return (
        <ul>
            {internal.map(createItem)}
            {external.length > 0 && <hr/>}
            {external.map(createItem)}
        </ul>
    )
}

